import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ExternalLink, Cpu, Dna, Lock, Zap, FileText, Microscope, Layers, AlertTriangle, BarChart } from 'lucide-react';

const LogicPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-28 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-cyan-400/30 mb-8 shadow-lg shadow-cyan-500/20">
                <Cpu className="h-5 w-5 text-cyan-400" />
                <span className="text-sm font-bold text-cyan-300 tracking-wide">RESEARCH INFRASTRUCTURE</span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.03em' }}>
                <span className="gradient-text">P.R.I.S.M.</span>
              </h1>
              
              <p className="text-2xl md:text-3xl font-semibold mb-4 text-cyan-300" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Programmable Research Integration & Systems Management
              </p>
              
              <p className="text-xl md:text-2xl font-medium mb-8 text-slate-300" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                A Unified Framework for Biological Orchestration
              </p>

              <div className="w-40 h-2 bg-gradient-to-r from-cyan-500 via-indigo-500 to-purple-500 mx-auto rounded-full shadow-lg mb-12"></div>

              {/* Description */}
              <div className="text-base md:text-lg text-slate-300 max-w-4xl mx-auto leading-relaxed mb-12 space-y-4">
                <p>
                  The Chaturvedi Lab developed P.R.I.S.M. as an internal response to the systemic friction inherent in modern discovery. By integrating the entire research lifecycle into a single digital thread, we have eliminated the fragmented workflows that traditionally stall synthetic biology.
                </p>
              </div>

              {/* Launch Button */}
              <a
                href="https://chaturvedilab.app/login"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-10 py-5 premium-gradient text-white rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105"
              >
                <Zap className="h-6 w-6" />
                Launch P.R.I.S.M.
                <ExternalLink className="h-5 w-5" />
              </a>

              {/* Access Note */}
              <div className="mt-6 inline-flex items-center gap-2 text-slate-400">
                <Lock className="h-4 w-4" />
                <span className="text-sm">Lab Member Login Required</span>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                The Architecture of Discovery
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                P.R.I.S.M. is the infrastructure that makes biological breakthroughs a scalable and reproducible reality
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Feature 1 - Translational Continuity */}
              <div className="glass-light rounded-2xl p-8 hover-lift border border-indigo-100">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl premium-gradient flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-500/30">
                    <FileText className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Translational Continuity
                    </h3>
                  </div>
                </div>
                <p className="text-slate-700 leading-relaxed text-lg">
                  Our <span className="font-semibold text-indigo-600">Paper to Protocol</span> engine collapses the timeline of methodology development, converting literature directly into validated bench workflows.
                </p>
              </div>

              {/* Feature 2 - Precision Engineering */}
              <div className="glass-light rounded-2xl p-8 hover-lift border border-cyan-100">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/30">
                    <Dna className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Precision Engineering
                    </h3>
                  </div>
                </div>
                <p className="text-slate-700 leading-relaxed text-lg">
                  From <span className="font-semibold text-cyan-600">CRISPR Design</span> and <span className="font-semibold text-cyan-600">Sequence Management</span> to real-time <span className="font-semibold text-cyan-600">Lineage Graphing</span>, we maintain absolute data provenance from initial construct to phenotypic readout.
                </p>
              </div>

              {/* Feature 3 - Autonomous Logistics */}
              <div className="glass-light rounded-2xl p-8 hover-lift border border-purple-100">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/30">
                    <Layers className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Autonomous Logistics
                    </h3>
                  </div>
                </div>
                <p className="text-slate-700 leading-relaxed text-lg">
                  High-stakes infrastructure—including <span className="font-semibold text-purple-600">LN2 and -80°C Smart Inventory</span>—is fully digitized, ensuring resource optimization and sample integrity.
                </p>
              </div>

              {/* Feature 4 - Institutional Intelligence */}
              <div className="glass-light rounded-2xl p-8 hover-lift border border-amber-100">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
                    <AlertTriangle className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Institutional Intelligence
                    </h3>
                  </div>
                </div>
                <p className="text-slate-700 leading-relaxed text-lg">
                  Through a dedicated <span className="font-semibold text-amber-600">Failure Dashboard</span>, we transform experimental setbacks into searchable data, providing a de-risked environment for high-impact research.
                </p>
              </div>

              {/* Feature 5 - Synthesized Output */}
              <div className="glass-light rounded-2xl p-8 hover-lift border border-emerald-100 md:col-span-2">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
                    <BarChart className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Synthesized Output
                    </h3>
                  </div>
                </div>
                <p className="text-slate-700 leading-relaxed text-lg">
                  Integrated <span className="font-semibold text-emerald-600">Data Analysis</span> and <span className="font-semibold text-emerald-600">Figure Maker</span> tools facilitate a lossless transition from the bench to the final manuscript, preserving data integrity at every step.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LogicPage;
